import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { WindowRef } from '@spartacus/core';
import { CarouselComponent, CarouselService } from '@spartacus/storefront';
import { BehaviorSubject, interval, Observable, Subscription } from 'rxjs';

export interface AutoplayConfig {
  delay: number;
  pauseOnMouseEnter: boolean;
  disableOnInteraction: boolean;
}

@Component({
  selector: 'app-interactive-carousel',
  templateUrl: './interactive-carousel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})

// TODO: fix focus setting on autoplay !

/**
 * InteractiveCarouselComponent extends CarouselComponent to provide an interactive carousel
 * with touch, swipe, and autoplay support.
 * Carousel items are rendered in slides, the number of items per slide is controlled by itemWidth and itemsPerSlide.
 * Previous/next buttons as well as the indicator-buttons can used to navigate the slides.
 *
 * @input title
 *   Optional title for the carousel, rendered as a heading if provided.
 *
 * @input items
 *   Array of Observables, (`items$`), to allow for lazy loading of items. Required.
 *
 * @input navigation
 *   Whether navigation controls (arrows) should be displayed. Defaults to `true`.
 *
 * @Input hideIndicators
 *   Whether to hide the slide indicators (dots) below the carousel. Defaults to `false`.
 *
 * @input showIndicatorTitles
 *   Whether to display titles on slide indicators. Defaults to `false`. If hideIndicators is false it has no effect.
 *
 * @input itemWidth
 *   Width of each item (e.g. "100%", "275px"). Used for dynamic sizing.
 *
 * @input itemsPerSlide
 *   Number of items to display per slide. If `null`, the carousel will determine the number dynamically by using the `itemWidth`.
 *   If set, it will override the dynamic sizing (and ignore itemWidth).
 *   Defaults to `null`.
 *
 * @input template
 *   TemplateRef for rendering each carousel item. Passed from parent via [template].
 *
 * @input autoplay
 *   Enables autoplay for the carousel. Can be a boolean or an `AutoplayConfig` object.
 *   Defaults to `false`.
 *
 * @input showPlayPauseButton
 *   Whether to show the play/pause button. Defaults to `false`.
 *
 * @input loop
 *   Whether the carousel should loop infinitely. Defaults to `true`.
 *
 * @input transitionDuration
 *   Duration of the slide transition animation in milliseconds. Defaults to `300`.
 *
 */
export class InteractiveCarouselComponent
  extends CarouselComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  @Input() loop = true;
  @Input() navigation = true;
  @Input() autoplay: AutoplayConfig | boolean = false;
  @Input() showIndicatorTitles = false;
  @Input() transitionDuration = 300;
  @Input() itemsPerSlide: number | null = null;
  @Input() showPlayPauseButton: boolean = false;
  @Output() clickEvent = new EventEmitter<Event>();
  @Output() slideChange = new EventEmitter<{
    slidesContainer: HTMLElement | null;
    activeIndex: number;
    lastVisibleIndex: number;
    type: 'manual' | 'automatic';
    direction: 'next' | 'previous' | 'goto';
    fromSlide: number;
    toSlide: number;
  }>();

  public slidesContainerElement: HTMLElement | null = null;
  private autoplaySubscription: Subscription | null = null;

  private autoplayPaused = false;
  private autoplayPermanentlyStopped = false;
  private interactionHappened = false;

  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private swipeThresholdDistance = 50;
  private swipeThresholdTime = 300;
  private isDragging = false;
  private wasSwiping = false; // Track if a swipe just occurred to suppress click

  private sizeSubscription: Subscription | null = null;
  private activeTimeouts: number[] = [];
  private intersectionObserver: IntersectionObserver | null = null;
  private isVisible: boolean = false;

  public isTransitioning = false;
  public firstClonedItem: Observable<any> | null = null;
  public lastClonedItem: Observable<any> | null = null;
  public size: number = 0;
  public slideWidth = 0;
  public sizeSubject: BehaviorSubject<number> = new BehaviorSubject<number>(1);

  // Computed size signal to reduce async pipe usage and layout shifts
  public sizeSignal = 1;

  private navigationContext: {
    type: 'manual' | 'automatic';
    direction: 'next' | 'previous' | 'goto';
    fromSlide: number;
    toSlide: number;
  } | null = null;

  constructor(
    protected override el: ElementRef,
    protected override service: CarouselService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private winRef: WindowRef
  ) {
    super(el, service);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Set initial size to prevent layout shift
    if (this.itemsPerSlide && this.itemsPerSlide > 0) {
      this.size = this.itemsPerSlide;
      this.sizeSignal = this.itemsPerSlide;
      this.sizeSubject.next(this.itemsPerSlide);
    }

    this.initializeItems();
    this.setupAutoplay();
  }

  ngAfterViewInit(): void {
    this.slidesContainerElement = this.el.nativeElement.querySelector('.slides');

    // Initialize intersection observer to track visibility
    this.initIntersectionObserver();

    if (this.slidesContainerElement && this.items.length > 1) {
      if (this.itemsPerSlide && this.itemsPerSlide > 0) {
        // Use fixed size, do not subscribe to dynamic size$
        this.handleSizeChange(this.itemsPerSlide);
      } else {
        this.size$ = this.service.getItemsPerSlide(this.slidesContainerElement, this.itemWidth);
        this.sizeSubscription = this.size$.subscribe({
          next: (size) => this.handleSizeChange(size),
          error: (error) => {
            console.error('Carousel size calculation error in ngAfterViewInit:', error);
            this.sizeSubscription?.unsubscribe();
            this.sizeSubscription = null;
          },
        });
      }
      this.setTransform(this.getVisualIndex(), 0);
    }
    // Initialize touch events if needed
    this.initTouchEvents();
  }

  override ngOnChanges(): void {
    super.ngOnChanges();
    if (this.slidesContainerElement) {
      this.initializeItems();
      // React to itemsPerSlide changes
      if (this.itemsPerSlide && this.itemsPerSlide > 0) {
        this.handleSizeChange(this.itemsPerSlide);
      } else {
        this.setTransform(this.getVisualIndex(), 0);
      }
      this.cdr.markForCheck();
      this.setupAutoplay();
    }
  }

  ngOnDestroy(): void {
    // Stop all timers and subscriptions
    this.stopAutoplay();
    this.sizeSubscription?.unsubscribe();
    this.sizeSubscription = null;

    // Disconnect intersection observer
    this.disconnectIntersectionObserver();

    // Clear all active timeouts
    this.clearAllTimeouts();

    // Complete BehaviorSubject to prevent memory leaks
    this.sizeSubject.complete();

    // Clear DOM references
    this.slidesContainerElement = null;
    this.firstClonedItem = null;
    this.lastClonedItem = null;

    // Reset navigation context
    this.navigationContext = null;

    // Clear touch tracking variables
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.isDragging = false;
    this.wasSwiping = false;
  }

  /**
   * Safely create a timeout and track it for cleanup
   */
  private safeSetTimeout(callback: () => void, delay: number): number {
    if (!this.winRef.isBrowser()) {
      return 0; // Return dummy ID for SSR
    }

    const timeoutId = this.winRef.nativeWindow!.setTimeout(() => {
      // Remove from active timeouts when it executes
      const index = this.activeTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.activeTimeouts.splice(index, 1);
      }
      callback();
    }, delay);

    this.activeTimeouts.push(timeoutId);
    return timeoutId;
  }

  /**
   * Clear all active timeouts
   */
  private clearAllTimeouts(): void {
    if (!this.winRef.isBrowser()) {
      this.activeTimeouts = [];
      return;
    }

    this.activeTimeouts.forEach((timeoutId) => {
      this.winRef.nativeWindow!.clearTimeout(timeoutId);
    });
    this.activeTimeouts = [];
  }

  /**
   * Initialize IntersectionObserver to track carousel visibility
   */
  private initIntersectionObserver(): void {
    if (!this.winRef.isBrowser() || !this.el?.nativeElement) {
      // Assume visible during SSR
      this.isVisible = true;
      return;
    }

    // Check if IntersectionObserver is available
    if (!('IntersectionObserver' in this.winRef.nativeWindow!)) {
      // Fallback: assume always visible if IntersectionObserver not supported
      this.isVisible = true;
      return;
    }

    try {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const wasVisible = this.isVisible;
            this.isVisible = entry.isIntersecting;

            // If carousel just became visible, resume autoplay if needed
            if (!wasVisible && this.isVisible) {
              if (this.isAutoplayEnabled() && !this.autoplayPermanentlyStopped && !this.shouldDisableAutoplay()) {
                this.resumeAutoplay();
              }
            }

            // If carousel just became hidden, pause autoplay
            if (wasVisible && !this.isVisible) {
              this.pauseAutoplay();
            }
          });
        },
        {
          root: null, // Use viewport as root
          rootMargin: '0px',
          threshold: 0.1, // Trigger when at least 10% of the carousel is visible
        }
      );

      this.intersectionObserver.observe(this.el.nativeElement);
    } catch (error) {
      console.error('Failed to initialize IntersectionObserver:', error);
      // Fallback: assume always visible
      this.isVisible = true;
    }
  }

  /**
   * Disconnect and cleanup IntersectionObserver
   */
  private disconnectIntersectionObserver(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  }

  private handleSizeChange(size: number): void {
    const oldSize = this.size;

    // If itemsPerSlide is set, always use it
    if (this.itemsPerSlide && this.itemsPerSlide > 0) {
      this.size = this.itemsPerSlide;
      this.sizeSignal = this.itemsPerSlide;
      this.sizeSubject.next(this.itemsPerSlide); // Always emit new size
    } else {
      this.size = size;
      this.sizeSignal = size;
      this.sizeSubject.next(size);
    }

    if (oldSize !== this.size && this.size > 0 && this.items.length > 0) {
      const group = Math.floor(this.activeSlide / this.size);
      const newActiveSlide = group * this.size;
      if (this.activeSlide !== newActiveSlide) {
        this.activeSlide = newActiveSlide;
      }
    }

    if (this.items.length > 0 && this.size > 0) {
      // Defer slideWidth measurement to avoid layout shifts
      // Use requestAnimationFrame to measure after layout is stable
      if (this.winRef.isBrowser() && this.slidesContainerElement) {
        requestAnimationFrame(() => {
          if (this.slidesContainerElement) {
            this.slideWidth = this.slidesContainerElement.clientWidth || 0;
          }
        });
      }
      this.setTransform(this.getVisualIndex(), 0);
      this.cdr.markForCheck();
    }
  }

  private initializeItems(): void {
    if (this.loop && this.items?.length > 1) {
      this.firstClonedItem = this.items[0];
      this.lastClonedItem = this.items[this.items.length - 1];
    } else {
      this.firstClonedItem = null;
      this.lastClonedItem = null;
    }

    this.activeSlide = this.items.length === 0 ? 0 : Math.max(0, Math.min(this.activeSlide, this.items.length - 1));
  }

  private getVisualIndex(index?: number): number {
    const slideIndex = index !== undefined ? index : this.activeSlide;

    let visualIndex: number;
    if (this.loop) {
      if (this.size === 1) {
        // For single items with loop, add 1 for cloned item at beginning
        visualIndex = slideIndex + 1;
      } else {
        // For multiple items with loop, calculate group index and add 1 for cloned group
        visualIndex = Math.floor(slideIndex / this.size) + 1;
      }
    } else {
      // For no loop, just calculate the group index
      visualIndex = Math.floor(slideIndex / this.size);
    }

    // Safety check - ensure value is valid
    if (isNaN(visualIndex) || !isFinite(visualIndex)) visualIndex = 0;

    return visualIndex;
  }

  private setTransform(index: number, duration: number): void {
    if (!this.slidesContainerElement) return;

    // The index parameter is already the visual slide group index from getVisualIndex()
    let transform = index * 100;

    // Use will-change hint before transition for smoother animations
    if (duration > 0 && this.winRef.isBrowser()) {
      this.renderer.setStyle(this.slidesContainerElement, 'will-change', 'transform');
    }

    this.renderer.setStyle(this.slidesContainerElement, 'transition-duration', `${duration}ms`);
    this.renderer.setStyle(this.slidesContainerElement, 'transform', `translateX(-${transform}%)`);

    if (duration === 0) {
      // Force a reflow to ensure the transform is applied immediately (browser only)
      if (this.winRef.isBrowser() && this.slidesContainerElement) {
        void this.slidesContainerElement.offsetWidth;
      }
      // Remove will-change after immediate transform
      if (this.winRef.isBrowser()) {
        this.renderer.removeStyle(this.slidesContainerElement, 'will-change');
      }
    } else if (duration > 0) {
      // Remove will-change after transition completes to free up resources
      this.safeSetTimeout(() => {
        if (this.slidesContainerElement && this.winRef.isBrowser()) {
          this.renderer.removeStyle(this.slidesContainerElement, 'will-change');
        }
      }, duration + 50);
    }
  }

  private setDragTransform(dragPercentage: number): void {
    if (!this.slidesContainerElement || this.isTransitioning) return;

    // Calculate current position (negative percentage) plus drag offset
    const basePosition = this.getVisualIndex() * 100;
    const transform = (basePosition - dragPercentage) * -1;

    // Apply transform with drag offset
    this.renderer.setStyle(this.slidesContainerElement, 'transform', `translateX(${transform}%)`);
  }

  /**
   * Ensures the carousel aligns to the nearest full slide
   * Called after interactions to prevent slides being partially visible
   * @param duration The duration of the snap animation in milliseconds
   * @param delay Optional delay before snapping to ensure proper alignment
   */
  private snapToActiveSlide(duration: number = 100, delay: number = 10): void {
    if (!this.slidesContainerElement) return;

    // Use safeSetTimeout to add a small delay before snapping
    // This ensures the browser has completed any ongoing rendering/animation before we align
    this.safeSetTimeout(() => {
      // Snap back to the current activeSlide position
      this.setTransform(this.getVisualIndex(), duration);
    }, delay);
  }

  private handleClonedSlideTransition(targetSlide: number): void {
    if (this.items.length <= 1) return;

    // Reset immediately to the real slide with no transition
    if (targetSlide < 0) {
      // Cloned last item is shown (at position 0), jump to the real last item
      this.activeSlide = this.items.length - 1;
      this.setTransform(this.getVisualIndex(), 0);
    } else if (targetSlide >= this.items.length) {
      // Cloned first item is shown (at position items.length+1), jump to the real first item
      this.activeSlide = 0;
      this.setTransform(this.getVisualIndex(), 0);
    }
  }

  /**
   * Go to the next slide in the carousel.
   */
  public nextSlide(): void {
    if (this.isTransitioning || this.items.length <= 1 || (!this.loop && this.activeSlide === this.items.length - 1)) {
      return;
    }

    const fromSlide = this.activeSlide;
    const toSlide = this.activeSlide + this.size;

    this.interactionHappened = true;
    this.navigationContext = { type: 'manual', direction: 'next', fromSlide, toSlide };
    this.goToSlide(toSlide);
  }

  /**
   * Go to the previous slide in the carousel.
   */
  public prevSlide(): void {
    if (this.isTransitioning || this.items.length <= 1 || (!this.loop && this.activeSlide === 0)) {
      return;
    }

    const fromSlide = this.activeSlide;
    const toSlide = this.activeSlide - this.size;

    this.interactionHappened = true;
    this.navigationContext = { type: 'manual', direction: 'previous', fromSlide, toSlide };
    this.goToSlide(toSlide);
  }

  /**
   * Go to the specified slide index in the carousel.
   * @param targetSlide The slide index to navigate to.
   */
  public goToSlide(targetSlide: number, isAutomatic: boolean = false): void {
    if (this.isTransitioning || !this.slidesContainerElement || this.items.length <= 1) {
      return;
    }

    // Skip automatic transitions if carousel is not visible
    if (isAutomatic && !this.isVisible) {
      return;
    }

    const fromSlide = this.activeSlide;

    // Set navigation context if not already set
    if (!this.navigationContext) {
      this.navigationContext = {
        type: isAutomatic ? 'automatic' : 'manual',
        direction: isAutomatic ? 'next' : 'goto',
        fromSlide,
        toSlide: targetSlide,
      };
    }

    // Check if any element in the current slide has focus before transition
    const shouldTransferFocus = isAutomatic && this.isVisible && this.shouldTransferSlideFocus();

    this.isTransitioning = true;
    this.setTransform(this.getVisualIndex(targetSlide), this.transitionDuration);

    // Force change detection to update DOM with new activeSlide
    // this.cdr.detectChanges();

    // Update activeSlide
    this.activeSlide = this.loop
      ? (targetSlide + this.items.length) % this.items.length
      : Math.max(0, Math.min(targetSlide, this.items.length - 1));

    const transitionTimeout = setTimeout(() => {
      if (!this.slidesContainerElement) return;

      this.isTransitioning = false;
      if (this.loop && this.size === 1) {
        this.handleClonedSlideTransition(targetSlide);
      } else {
        // Make sure we're properly aligned to the current activeSlide
        this.snapToActiveSlide(0, 20);
      }

      if (shouldTransferFocus) {
        this.safeSetTimeout(() => {
          this.transferFocus();
        }, 20);
      }

      this.emitSlideChange();
      this.cdr.markForCheck();
    }, this.transitionDuration);

    // Track this timeout for cleanup
    this.activeTimeouts.push(transitionTimeout as unknown as number);
  }

  /**
   * Go to the slide containing the item at the specified index.
   * @param index The item index to select.
   */
  public goToItem(index: number): void {
    const targetSlide = Math.floor(index / this.size) * this.size;
    const fromSlide = this.activeSlide;

    this.navigationContext = { type: 'manual', direction: 'goto', fromSlide, toSlide: targetSlide };
    this.goToSlide(targetSlide);
  }

  /**
   * Select the item at the specified index and update the carousel.
   * @param index The item index to select.
   */
  public selectItem(index: number): void {
    if (this.isTransitioning || this.items.length <= 1) {
      return;
    }
    this.setItemsClassName(index, 'selected');
    this.goToItem(index);
  }

  /**
   * Set a class name on the item at the specified index, removing it from others.
   * @param index The item index.
   * @param className The class name to set.
   */
  public setItemsClassName(index: number, className: string): void {
    const items = this.slidesContainerElement?.querySelectorAll('.item');
    if (!items) return;

    // forEach is not available in SSR, so check if it's not available
    if (typeof (items as any).forEach === 'function') {
      items.forEach((item, i) => {
        if (i === index) item.classList.add(className);
        else item.classList.remove(className);
      });
    } else {
      // SSR fallback: NodeList may not have forEach, so use a classic for loop
      for (let i = 0; i < (items as any).length; i++) {
        const item = items[i];
        if (i === index) item.classList.add(className);
        else item.classList.remove(className);
      }
    }
  }

  // Touch event handlers
  private initTouchEvents(): void {
    if (!this.el?.nativeElement || !this.slidesContainerElement) return;

    // Use native addEventListener for passive listeners
    this.slidesContainerElement.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
    this.slidesContainerElement.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
    this.slidesContainerElement.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
    this.slidesContainerElement.addEventListener('touchcancel', this.onTouchCancel.bind(this), { passive: true });
  }

  private onTouchStart(event: TouchEvent): void {
    if (this.items.length <= 1 || this.slideWidth === 0 || event.touches.length !== 1) return;

    this.touchStartX = event.touches[0].clientX;
    this.touchStartY = event.touches[0].clientY;
    this.touchStartTime = Date.now();
    this.isDragging = true;
    this.wasSwiping = false;

    this.renderer.setStyle(this.slidesContainerElement, 'transition-duration', '0ms');
    if (this.isAutoplayEnabled()) {
      this.pauseAutoplay();
    }
  }

  private onTouchMove(event: TouchEvent): void {
    if (!this.touchStartX || this.items.length <= 1 || !this.isDragging) return;

    const currentX = event.touches[0].clientX;
    const currentY = event.touches[0].clientY;
    const deltaX = currentX - this.touchStartX;
    const deltaY = currentY - this.touchStartY;

    // Calculate horizontal and vertical movement
    const horizontalMovement = Math.abs(deltaX);
    const verticalMovement = Math.abs(deltaY);

    // Only apply drag transform if movement is primarily horizontal
    if (horizontalMovement > verticalMovement * 1.2) {
      // Ensure slideWidth is measured before calculating drag
      if (this.slideWidth === 0 && this.slidesContainerElement && this.winRef.isBrowser()) {
        this.slideWidth = this.slidesContainerElement.clientWidth || 0;
      }

      let dragPercentage = (deltaX / this.slideWidth) * 100;
      if (dragPercentage > 100) dragPercentage = 100;
      if (dragPercentage < -100) dragPercentage = -100;

      if (!this.loop) {
        const atStart = this.activeSlide === 0 && dragPercentage > 0;
        const atEnd = this.activeSlide >= this.items.length - this.size && dragPercentage < 0;
        if (atStart || atEnd) {
          dragPercentage *= 0.2; // Slow down drag when at the start or end
        }
      }

      // Use requestAnimationFrame for smoother dragging
      requestAnimationFrame(() => {
        this.setDragTransform(dragPercentage);
      });
      event.preventDefault();
    }
  }

  private onTouchEnd(event: TouchEvent): void {
    if (!this.touchStartX || !this.slideWidth || this.items.length <= 1) return;

    const endX = event.changedTouches[0].clientX;
    const endY = event.changedTouches[0].clientY;
    const deltaX = endX - this.touchStartX;
    const deltaY = endY - this.touchStartY;
    const touchDuration = Date.now() - this.touchStartTime;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const horizontalPercentage = Math.abs(deltaX) / this.slideWidth;

    // Prevent swiping beyond the first or last slide when not looping
    if (
      !this.loop &&
      ((this.activeSlide === 0 && deltaX > 0) || (this.activeSlide >= this.items.length - this.size && deltaX < 0))
    ) {
      // Snap back to the current slide
      requestAnimationFrame(() => {
        this.setTransform(this.getVisualIndex(), 100);
      });
      return;
    }

    // Determine if this was a valid swipe gesture
    const isHorizontalSwipe =
      Math.abs(deltaX) > this.swipeThresholdDistance && Math.abs(deltaX) > Math.abs(deltaY) * 1.2;
    const isQuickMovement = touchDuration <= this.swipeThresholdTime;
    // Add condition for slow but significant swipe (over 50%)
    const isSignificantSwipe = horizontalPercentage > 0.5 && Math.abs(deltaX) > this.swipeThresholdDistance;

    if ((isHorizontalSwipe && isQuickMovement) || isSignificantSwipe) {
      // It's a horizontal swipe - direction is important here
      // deltaX positive means swiped right (finger moved right) = show previous slide
      // deltaX negative means swiped left (finger moved left) = show next slide
      this.interactionHappened = true;
      this.wasSwiping = true;

      deltaX > 0 ? this.prevSlide() : this.nextSlide();

      // Check if autoplay should be disabled after interaction
      if (this.isAutoplayEnabled() && this.getAutoplayConfig().disableOnInteraction) {
        this.stopAutoplay();
      }
    } else {
      // Use both requestAnimationFrame and a delay to ensure proper alignment
      // Always snap back to the position based on the current activeSlide
      requestAnimationFrame(() => {
        // Use a slightly longer delay for touch events to ensure stability
        this.snapToActiveSlide(100, 30);
      });

      // For minor movements, don't consider it a swipe (allow click events)
      this.wasSwiping = distance > this.swipeThresholdDistance / 2;

      // Resume autoplay if appropriate
      if (
        this.isAutoplayEnabled() &&
        !this.getAutoplayConfig().disableOnInteraction &&
        !this.autoplayPermanentlyStopped
      ) {
        this.resumeAutoplay();
      }
    }

    // Reset touch values
    this.isDragging = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;

    // The wasSwiping flag is kept until the potential click event
  }

  private onTouchCancel(): void {
    if (!this.slidesContainerElement) return;

    // Reset transform with transition animation, ensuring full slide alignment
    this.snapToActiveSlide(this.transitionDuration, 20);

    // Resume autoplay if appropriate
    if (
      this.isAutoplayEnabled() &&
      !this.getAutoplayConfig().disableOnInteraction &&
      !this.autoplayPermanentlyStopped
    ) {
      this.resumeAutoplay();
    }

    // Reset touch tracking variables
    this.isDragging = false;
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.wasSwiping = false; // Reset after cancel
  }

  /**
   * Handle keyboard events on carousel container for slide navigation.
   */
  public onCarouselKeydown(event: KeyboardEvent): void {
    if (!this.items?.length) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      this.pauseAutoplay();
      event.preventDefault();
      if (event.key === 'ArrowLeft') {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
      this.safeSetTimeout(() => {
        this.transferFocus();
        this.resumeAutoplay();
      }, this.transitionDuration + 50);
    }
  }

  /**
   * Handle keyboard events on indicator buttons for navigation.
   */
  public onIndicatorKeydown(event: KeyboardEvent, slideIndex: number): void {
    if (!this.winRef.isBrowser()) {
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.pauseAutoplay();

      const indicators: HTMLButtonElement[] = Array.from(
        this.el.nativeElement.querySelectorAll('.indicators button[role="tab"]')
      );

      // Skip document.activeElement access during SSR
      if (!this.winRef.isBrowser()) {
        return;
      }

      const current = this.winRef.nativeWindow!.document.activeElement as HTMLButtonElement;
      const currentIndex = indicators.indexOf(current);
      if (currentIndex === -1) return;

      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      const targetIndex = (currentIndex + delta + indicators.length) % indicators.length;

      // Navigate to the corresponding slide
      const targetSlideIndex = targetIndex * this.size;
      this.goToSlide(targetSlideIndex);

      // Focus the target indicator after transition
      this.safeSetTimeout(() => {
        indicators[targetIndex]?.focus();
      }, this.transitionDuration + 50);

      this.resumeAutoplay();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.pauseAutoplay();
      this.goToSlide(slideIndex);
      this.resumeAutoplay();
    }
  }

  private stopAutoplayTimer(): void {
    this.autoplaySubscription?.unsubscribe();
    this.autoplaySubscription = null;
  }

  private pauseAutoplay(): void {
    this.autoplayPaused = true;
    this.stopAutoplayTimer();
  }

  private resumeAutoplay(): void {
    if (!this.autoplayPermanentlyStopped && !this.shouldDisableAutoplay()) {
      this.autoplayPaused = false;
      this.setupAutoplay();
    }
  }

  /**
   * Permanently stop autoplay for the carousel.
   */
  public stopAutoplay(): void {
    this.autoplayPermanentlyStopped = true;
    this.autoplayPaused = false;
    this.stopAutoplayTimer();
  }

  /**
   * Start or resume autoplay for the carousel if it was permanently stopped.
   */
  public startAutoplay(): void {
    this.autoplayPermanentlyStopped = false;
    this.autoplayPaused = false;
    this.setupAutoplay();
  }

  public isAutoplayEnabled(): boolean {
    return !!this.autoplay && this.items.length > 1;
  }

  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.isAutoplayEnabled() && this.getAutoplayConfig().pauseOnMouseEnter) {
      this.pauseAutoplay();
    }
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.isAutoplayEnabled() && this.getAutoplayConfig().pauseOnMouseEnter && !this.shouldDisableAutoplay()) {
      this.resumeAutoplay();
    }
  }

  // Suppress clicks that happen right after a swipe
  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (this.isDragging || this.wasSwiping) {
      event.preventDefault();
      event.stopImmediatePropagation?.();
      this.wasSwiping = false; // Reset after suppressing
      return;
    }
    this.clickEvent.emit(event);
  }

  /**
   * Toggle autoplay on/off
   */
  public toggleAutoplay(): void {
    if (this.autoplayPermanentlyStopped) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
    this.cdr.markForCheck();
  }

  /**
   * Get current autoplay state for UI display
   */
  public isAutoplayActive(): boolean {
    return this.isAutoplayEnabled() && !this.autoplayPermanentlyStopped;
  }

  /**
   * Check if autoplay is temporarily paused (e.g., on mouse hover)
   * Only returns true if autoplay is enabled and active, but temporarily paused
   */
  public isAutoplayPaused(): boolean {
    return this.autoplayPaused && this.isAutoplayEnabled() && !this.autoplayPermanentlyStopped;
  }

  private getAutoplayConfig(): AutoplayConfig {
    const defaultConfig: AutoplayConfig = { delay: 5000, pauseOnMouseEnter: true, disableOnInteraction: true };
    return typeof this.autoplay === 'boolean' ? defaultConfig : { ...defaultConfig, ...this.autoplay };
  }

  private shouldDisableAutoplay(): boolean {
    return this.interactionHappened && this.getAutoplayConfig().disableOnInteraction;
  }

  private setupAutoplay(): void {
    if (
      !this.isAutoplayEnabled() ||
      this.autoplayPaused ||
      this.shouldDisableAutoplay() ||
      this.autoplayPermanentlyStopped ||
      !this.winRef.isBrowser() ||
      !this.isVisible // Don't start autoplay if carousel is not visible
    ) {
      return;
    }

    this.stopAutoplayTimer();
    const config = this.getAutoplayConfig();
    this.autoplaySubscription = interval(config.delay).subscribe({
      next: () => {
        if (!this.isTransitioning) {
          this.goToSlide(this.activeSlide + 1, true); // Mark as automatic
        }
      },
      error: (error) => {
        console.error('Autoplay interval error:', error);
        this.stopAutoplayTimer();
      },
    });
  }

  private emitSlideChange(): void {
    const lastVisibleIndex = Math.min(this.activeSlide + this.size - 1, this.items.length - 1);
    const context = this.navigationContext || {
      type: 'manual',
      direction: 'goto',
      fromSlide: this.activeSlide,
      toSlide: this.activeSlide,
    };

    this.slideChange.emit({
      slidesContainer: this.slidesContainerElement,
      activeIndex: this.activeSlide,
      lastVisibleIndex,
      ...context,
    });

    // Reset navigation context after emitting
    this.navigationContext = null;
  }

  /**
   * Check if focus should be transferred from inactive to active slide
   */
  private shouldTransferSlideFocus(): boolean {
    if (!this.winRef.isBrowser() || !this.isVisible) {
      return false;
    }

    const activeElement = this.winRef.nativeWindow!.document.activeElement as HTMLElement | null;
    if (!activeElement || !this.slidesContainerElement) return false;

    // Check if the focused element is within the carousel component
    return this.el.nativeElement.contains(activeElement);
  }

  /**
   * Transfer focus appropriately based on the current focus context
   */
  private transferFocus(): void {
    if (!this.winRef.isBrowser() || !this.isVisible) {
      return;
    }

    const activeElement = this.winRef.nativeWindow!.document.activeElement as HTMLElement | null;
    const activeSlide = this.slidesContainerElement?.querySelector('.slide.active') as HTMLElement | null;
    if (!activeElement || !activeSlide) return;

    // Check if an indicator has focus & move focus to the new active indicator
    const indicatorContainer = this.el.nativeElement.querySelector('.indicators');
    if (indicatorContainer?.contains(activeElement)) {
      const activeIndicator = indicatorContainer.querySelector(
        `button:nth-child(${this.activeSlide + 1})`
      ) as HTMLElement;
      activeIndicator?.focus();
      return;
    }

    // find the first focusable element in the active slide & move focus
    const focusableElement = this.findFirstFocusableElement(activeSlide);
    if (focusableElement) {
      focusableElement.focus();
      return;
    }

    // Fallback: focus the carousel container if no focusable element found
    if (this.slidesContainerElement) {
      this.el.nativeElement.querySelector('.carousel-panel')?.focus();
    }
  }

  /**
   * Find the first focusable element within a container
   */
  private findFirstFocusableElement(container: HTMLElement): HTMLElement | null {
    const focusableSelectors = [
      'button:not([disabled]):not([tabindex="-1"])',
      'a[href]:not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"])',
      '[contenteditable="true"]',
    ].join(', ');

    const focusableElements = container.querySelectorAll(focusableSelectors);

    for (const element of Array.from(focusableElements)) {
      const el = element as HTMLElement;

      // Check if element is visible and properly focusable
      if (this.isElementFocusable(el)) {
        return el;
      }
    }

    return null;
  }

  /**
   * Check if an element is currently focusable and visible
   */
  private isElementFocusable(element: HTMLElement): boolean {
    // Skip DOM measurements during SSR to prevent layout shift issues
    if (!this.winRef.isBrowser()) {
      // In SSR, assume element is focusable if it exists (will be checked properly in browser)
      return true;
    }

    // Check basic visibility
    if (element.offsetWidth === 0 && element.offsetHeight === 0) {
      return false;
    }

    // Check if element is hidden
    const computedStyle = this.winRef.nativeWindow!.getComputedStyle(element);
    if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || computedStyle.opacity === '0') {
      return false;
    }

    // Check ARIA attributes
    if (
      element.hasAttribute('inert') ||
      element.getAttribute('aria-hidden') === 'true' ||
      element.getAttribute('aria-disabled') === 'true'
    ) {
      return false;
    }

    return true;
  }

  /**
   * Get the number of slides based on the current size.
   * @param size Number of items per slide.
   */
  public getNumberOfSlides(size: number): number {
    return Math.ceil(this.items.length / size);
  }

  /**
   * Recalculate and update the carousel's layout and visible items.
   */
  public recalculateLayout(): void {
    if (!this.slidesContainerElement) return;

    // Clean up previous subscription to avoid leaks
    this.sizeSubscription?.unsubscribe();
    this.sizeSubscription = null;

    if (this.itemsPerSlide && this.itemsPerSlide > 0) {
      this.handleSizeChange(this.itemsPerSlide);
    } else if (this.service && this.itemWidth) {
      this.size$ = this.service.getItemsPerSlide(this.slidesContainerElement, this.itemWidth);
      this.sizeSubscription = this.size$.subscribe({
        next: (size) => this.handleSizeChange(size),
        error: (error) => {
          console.error('Carousel size calculation error:', error);
          this.sizeSubscription?.unsubscribe();
          this.sizeSubscription = null;
        },
      });
    }

    // Always force reflow and mark for check to ensure layout updates (browser only)
    if (this.winRef.isBrowser() && this.slidesContainerElement) {
      void this.slidesContainerElement.offsetWidth;
    }

    // Ensure the carousel is aligned to the nearest full slide
    this.snapToActiveSlide(0, 20);

    this.cdr.markForCheck();
  }
}
